import {
  createRouter,
  createRootRoute,
  createRoute,
  Navigate,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { AppShell } from './AppShell';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { validateAnalyticsSearch } from '@/features/analytics/searchParams';
import { AnalyticsLayout } from '@/features/analytics/components/AnalyticsLayout';
import { AnalyticsOverviewPage } from '@/features/analytics/components/AnalyticsOverviewPage';
import { AnalyticsContentPage } from '@/features/analytics/components/AnalyticsContentPage';
import { AnalyticsAudiencePage } from '@/features/analytics/components/AnalyticsAudiencePage';
import { AnalyticsDeliveryPage } from '@/features/analytics/components/AnalyticsDeliveryPage';
import { AnalyticsSubaccountsPage } from '@/features/analytics/components/AnalyticsSubaccountsPage';
import { LegacyReportsPage } from '@/features/legacy-reports/components/LegacyReportsPage';
import { ProtectedRoute } from '@/shared/auth/ProtectedRoute';
import { LoginPage } from '@/features/auth/components/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/components/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/components/ResetPasswordPage';
import { VideosLayout } from '@/features/media/components/VideosLayout';
import { VideosPage } from '@/features/media/components/VideosPage';
import { VideoDetailPage } from '@/features/media/components/VideoDetailPage';
import { PlaylistsPage } from '@/features/playlists/components/PlaylistsPage';
import { PlaylistDetailPage } from '@/features/playlists/components/PlaylistDetailPage';
import { StreamsLayout } from '@/features/streams/components/StreamsLayout';
import { StreamsPage } from '@/features/streams/components/StreamsPage';
import { StreamDetailPage } from '@/features/streams/components/StreamDetailPage';
import { SubAccountStreamsPage } from '@/features/streams/subaccounts/SubAccountStreamsPage';
import { PlayersPage } from '@/features/players/components/PlayersPage';
import { PlayerDetailPage } from '@/features/players/components/PlayerDetailPage';
import { FeedsLayout } from '@/features/feeds/components/FeedsLayout';
import { FeedsPage } from '@/features/feeds/components/FeedsPage';
import { FeedDetailPage } from '@/features/feeds/components/FeedDetailPage';
import { AppsLayout } from '@/features/apps/components/AppsLayout';
import { AppsPage } from '@/features/apps/components/AppsPage';
import { AppDetailPage } from '@/features/apps/components/AppDetailPage';
import { AppsContentLayout } from '@/features/apps-content/components/AppsContentLayout';
import { AppContentPage } from '@/features/apps-content/components/AppContentPage';
import { ContentGroupDetailPage } from '@/features/apps-content/components/ContentGroupDetailPage';
import { CategoryDetailPage } from '@/features/apps-content/components/CategoryDetailPage';
import { SeriesDetailPage } from '@/features/apps-content/components/SeriesDetailPage';
import { AccountLayout } from '@/features/account/components/AccountLayout';
import { BillingPage } from '@/features/account/components/billing/BillingPage';
import { TrialUpgradePage } from '@/features/account/components/billing/TrialUpgradePage';
import { UsagePage } from '@/features/account/components/usage/UsagePage';
import { TeamPage } from '@/features/account/components/team/TeamPage';
import { TeamMemberPage } from '@/features/account/components/team/TeamMemberPage';
import { SubAccountsPage } from '@/features/account/components/sub-accounts/SubAccountsPage';
import { SubAccountPage } from '@/features/account/components/sub-accounts/SubAccountPage';
import { UploadProfilesPage } from '@/features/account/components/upload-profiles/UploadProfilesPage';
import { UploadPage } from '@/features/upload/components/UploadPage';

// Root route - no component, just a wrapper
const rootRoute = createRootRoute();

// Public routes (no auth required)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

// Protected layout (requires auth)
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedRoute,
});

// App shell wraps all authenticated content
const appShellRoute = createRoute({
  getParentRoute: () => protectedRoute,
  id: 'app-shell',
  component: AppShell,
});

// Dashboard (home). Upload-only users go straight to their /upload home
// (before the analytics redirect — avoids a double hop). Otherwise, when the
// analytics flag is on, home redirects to the stable canonical /analytics
// path; else the placeholder stub stays.
function DashboardHome() {
  const { isUploadOnly } = useAuth();
  if (isUploadOnly) {
    return <Navigate to="/upload" />;
  }
  if (FEATURE_FLAGS.analyticsEnabled) {
    return <Navigate to="/analytics" />;
  }
  return (
    <div style={{ padding: '1rem' }}>
      <h1>Dashboard</h1>
      <p>Welcome! Auth is working. Navigate to Videos to see the media grid.</p>
    </div>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/',
  component: DashboardHome,
});

// Full-page upload experience — home surface for upload-only users, reachable
// by any ingest-capable user (useAppRouteGuard redirects non-ingest users).
const uploadRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/upload',
  component: UploadPage,
});

// Analytics routes (Phase 5 Sequence 8) — registered only when the build flag
// is on; scope + range live in typed search params on the layout route.
const analyticsLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/analytics',
  component: AnalyticsLayout,
  // SearchSchemaInput keeps navigation search-optional (defaults fill in).
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) =>
    validateAnalyticsSearch(search),
});

const analyticsIndexRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/',
  component: AnalyticsOverviewPage,
});

const analyticsContentRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/content',
  component: AnalyticsContentPage,
});

const analyticsAudienceRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/audience',
  component: AnalyticsAudiencePage,
});

const analyticsDeliveryRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/delivery',
  component: AnalyticsDeliveryPage,
});

const analyticsSubaccountsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: '/subaccounts',
  component: AnalyticsSubaccountsPage,
});

// Legacy Reports compatibility surface — registered only when its flag is on.
const legacyReportsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/legacy-reports',
  component: LegacyReportsPage,
});

// Videos layout route — SelectionProvider wraps both list and detail
const videosLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/videos',
  component: VideosLayout,
});

// Videos index (list page)
const videosIndexRoute = createRoute({
  getParentRoute: () => videosLayoutRoute,
  path: '/',
  component: VideosPage,
});

// Video detail route
const videoDetailRoute = createRoute({
  getParentRoute: () => videosLayoutRoute,
  path: '/$id',
  component: VideoDetailPage,
});

// Playlists routes
const playlistsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/playlists',
  component: PlaylistsPage,
});

const playlistDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/playlists/$id',
  component: PlaylistDetailPage,
});

// Streams layout route — SelectionProvider wraps both list and detail
const streamsLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/streams',
  component: StreamsLayout,
});

// Streams index (list page)
const streamsIndexRoute = createRoute({
  getParentRoute: () => streamsLayoutRoute,
  path: '/',
  component: StreamsPage,
});

// Sub-account streams (master-account monitoring view) — static path outranks /$id
const streamsSubAccountsRoute = createRoute({
  getParentRoute: () => streamsLayoutRoute,
  path: '/sub-accounts',
  component: SubAccountStreamsPage,
});

// Stream detail route
const streamDetailRoute = createRoute({
  getParentRoute: () => streamsLayoutRoute,
  path: '/$id',
  component: StreamDetailPage,
});

// Players routes
const playersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/players',
  component: PlayersPage,
});

const playerDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/players/$id',
  component: PlayerDetailPage,
});

// Feeds layout route -- SelectionProvider wraps both list and detail
const feedsLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/feeds',
  component: FeedsLayout,
});

const feedsIndexRoute = createRoute({
  getParentRoute: () => feedsLayoutRoute,
  path: '/',
  component: FeedsPage,
});

const feedDetailRoute = createRoute({
  getParentRoute: () => feedsLayoutRoute,
  path: '/$id',
  component: FeedDetailPage,
});

// Apps layout route — wraps apps list and detail
const appsLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/apps',
  component: AppsLayout,
});

const appsIndexRoute = createRoute({
  getParentRoute: () => appsLayoutRoute,
  path: '/',
  component: AppsPage,
});

const appDetailRoute = createRoute({
  getParentRoute: () => appsLayoutRoute,
  path: '/$id',
  component: AppDetailPage,
});

// Apps content layout — nested under /apps/content
const appsContentLayoutRoute = createRoute({
  getParentRoute: () => appsLayoutRoute,
  path: '/content',
  component: AppsContentLayout,
});

const appsContentIndexRoute = createRoute({
  getParentRoute: () => appsContentLayoutRoute,
  path: '/',
  component: AppContentPage,
});

const contentGroupDetailRoute = createRoute({
  getParentRoute: () => appsContentLayoutRoute,
  path: '/groups/$id',
  component: ContentGroupDetailPage,
});

const categoryDetailRoute = createRoute({
  getParentRoute: () => appsContentLayoutRoute,
  path: '/categories/$id',
  component: CategoryDetailPage,
});

const seriesDetailRoute = createRoute({
  getParentRoute: () => appsContentLayoutRoute,
  path: '/series/$id',
  component: SeriesDetailPage,
});

// Account layout route — wraps all account sub-pages
const accountLayoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/account',
  component: AccountLayout,
});

const billingRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/billing',
  component: BillingPage,
});

const usageRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/usage',
  component: UsagePage,
});

const upgradeTrialRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/upgrade-trial',
  component: TrialUpgradePage,
});

const teamRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/team',
  component: TeamPage,
});

const teamNewRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/team/new',
  component: TeamMemberPage,
});

const teamDetailRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/team/$id',
  component: TeamMemberPage,
});

const subAccountsRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/sub-accounts',
  component: SubAccountsPage,
});

const subAccountNewRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/sub-accounts/new',
  component: SubAccountPage,
});

const subAccountDetailRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/sub-accounts/$id',
  component: SubAccountPage,
});

const uploadProfilesRoute = createRoute({
  getParentRoute: () => accountLayoutRoute,
  path: '/upload-profiles',
  component: UploadProfilesPage,
});

// Analytics/legacy route subtrees. The full tuples below type the router so
// navigation targets stay stable; at runtime the flag-gated entries are
// included only when their build flag is true (route registration gating per
// ADR-003 §1 — direct navigation with a flag off 404s via not-found handling).
// The delivery ("CDN Delivery") view rides its own flag in addition to the
// analytics flag — staged dark at launch, flipped on post-parity (ADR-010 §3);
// AnalyticsLayout gates its tab with the same flag.
const analyticsRouteTree = analyticsLayoutRoute.addChildren([
  analyticsIndexRoute,
  analyticsContentRoute,
  analyticsAudienceRoute,
  ...(FEATURE_FLAGS.analyticsDeliveryEnabled ? [analyticsDeliveryRoute] : []),
  analyticsSubaccountsRoute,
] as [
  typeof analyticsIndexRoute,
  typeof analyticsContentRoute,
  typeof analyticsAudienceRoute,
  typeof analyticsDeliveryRoute,
  typeof analyticsSubaccountsRoute,
]);

const flaggedRoutes = [
  ...(FEATURE_FLAGS.analyticsEnabled ? [analyticsRouteTree] : []),
  ...(FEATURE_FLAGS.legacyReportsEnabled ? [legacyReportsRoute] : []),
] as [typeof analyticsRouteTree, typeof legacyReportsRoute];

// Build route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    appShellRoute.addChildren([
      dashboardRoute,
      uploadRoute,
      ...flaggedRoutes,
      videosLayoutRoute.addChildren([
        videosIndexRoute,
        videoDetailRoute,
      ]),
      playlistsRoute,
      playlistDetailRoute,
      streamsLayoutRoute.addChildren([
        streamsIndexRoute,
        streamsSubAccountsRoute,
        streamDetailRoute,
      ]),
      playersRoute,
      playerDetailRoute,
      feedsLayoutRoute.addChildren([
        feedsIndexRoute,
        feedDetailRoute,
      ]),
      appsLayoutRoute.addChildren([
        appsIndexRoute,
        appDetailRoute,
        appsContentLayoutRoute.addChildren([
          appsContentIndexRoute,
          contentGroupDetailRoute,
          categoryDetailRoute,
          seriesDetailRoute,
        ]),
      ]),
      accountLayoutRoute.addChildren([
        billingRoute,
        usageRoute,
        upgradeTrialRoute,
        teamRoute,
        teamNewRoute,
        teamDetailRoute,
        subAccountsRoute,
        subAccountNewRoute,
        subAccountDetailRoute,
        uploadProfilesRoute,
      ]),
    ]),
  ]),
]);

// Create and export router
export const router = createRouter({ routeTree });

// Type safety for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
